import React from "react";
import { Link, useLocation } from "react-router-dom";
import { CalendarDays, ChevronLeft, Home, Inbox, Briefcase, Menu } from "lucide-react";
import { useLanguage } from "../components/ui/LanguageProvider";
import { MobileShellContext } from "./MobileShellContext";
import PullToRefresh from "./PullToRefresh";
import MobileFab from "./MobileFab";
import NotificationBell from "../components/layout/NotificationBell";
import type { FabMode } from "./MobileFab";
import { EASE } from "../lib/motion";
import "./mobileShell.css";
import "./installPrompt";

/**
 * 모바일 셸 (PWA) — 767px 이하에서 CRMLayout 이 사이드바·상단 바 대신 이걸로 감싼다.
 * 본문은 PC 와 같은 실제 화면(<Outlet/>)이다. 셸은 앱바(제목·뒤로가기)와 하단 탭만 맡는다.
 *  - 하단 탭 5개: 홈 / 리드 / 기회 / 일정 / 더보기. 나머지 모든 메뉴는 더보기에 있다.
 *  - ★ 탭 줄은 grid 가 아니라 flex — mobile.css 가 폰 폭에서 .grid-cols-* 를 1열로 바꾼다.
 * 색·크기는 기존 토큰 클래스만. 루트에 ds-scope (CLAUDE.md).
 * 모션(docs/MOBILE_WEBAPP.md §7, 0.15–0.35초 · 동작 줄이기면 끔):
 *  - 하단 탭 알약 인디케이터(활성 탭 아이콘 뒤, ease-pop 으로 미끄러짐)
 *  - 화면 전환(경로가 바뀌면 본문이 8px 옆에서 페이드 인)
 *  - 큰 제목(상세가 아닌 화면 — 본문 맨 위 28px, 40px 넘게 내리면 앱바 작은 제목으로 바뀜)
 *  - 당겨서 새로고침(PullToRefresh) · FAB(MobileFab — 홈 스피드 다이얼 / 다른 화면 확장형)
 */
export const MOBILE_TABS = [
  { to: "/crm/dashboard", icon: Home, ko: "홈", en: "Home" },
  { to: "/crm/leads", icon: Inbox, ko: "리드", en: "Leads" },
  { to: "/crm/opportunities", icon: Briefcase, ko: "기회", en: "Deals" },
  { to: "/crm/calendar", icon: CalendarDays, ko: "일정", en: "Calendar" },
  { to: "/crm/more", icon: Menu, ko: "더보기", en: "More" },
] as const;

/** 지금 경로가 어느 탭에 속하는지 — 앞 4개에 안 걸리면 더보기. */
function activeTab(pathname: string): string {
  const hit = MOBILE_TABS.slice(0, 4).find((tab) => pathname === tab.to || pathname.startsWith(tab.to + "/"));
  return hit ? hit.to : "/crm/more";
}

/** 셸이 떠 있는 동안 html.in-mobile-shell — 포털로 뜨는 모달·패널도 모바일 보정을 받는다(mobileShell.css). */
function useShellClass() {
  React.useEffect(() => {
    const root = document.documentElement;
    root.classList.add("in-mobile-shell");
    return () => root.classList.remove("in-mobile-shell", "kb-open");
  }, []);
}

/**
 * 키보드가 열렸는지 — 입력칸에 포커스가 있고 visualViewport 가 가장 컸던 높이보다 150px 넘게 줄었을 때만 kb-open(하단 탭 숨김).
 * iOS 는 키보드가 fixed 하단 바를 덮는다.
 * ★ 높이만 보면 안 된다: 안드로이드에서 주소창 접힘·확대 상태로 높이가 달라져 키보드 없이 하단 탭이 사라졌다(실기기 신고).
 */
function isEditable(el: Element | null): boolean {
  if (!el) return false;
  const h = el as HTMLElement;
  if (h.isContentEditable) return true;
  if (el.tagName === "TEXTAREA" || el.tagName === "SELECT") return true;
  if (el.tagName !== "INPUT") return false;
  return !/^(checkbox|radio|button|submit|reset|range|color|file|image)$/i.test((el as HTMLInputElement).type);
}
function useKeyboardOpen() {
  React.useEffect(() => {
    const vv = window.visualViewport;
    const root = document.documentElement;
    let maxH = vv?.height ?? window.innerHeight;
    const update = () => {
      const h = vv?.height ?? window.innerHeight;
      if (!isEditable(document.activeElement)) { maxH = Math.max(maxH, h); root.classList.remove("kb-open"); return; }
      root.classList.toggle("kb-open", maxH - h > 150);
    };
    const onFocusOut = () => window.setTimeout(update, 50);
    vv?.addEventListener("resize", update);
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      vv?.removeEventListener("resize", update);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", onFocusOut);
      root.classList.remove("kb-open");
    };
  }, []);
}

/**
 * 본문 스크롤 위치 — 셸은 본문 하나가 스크롤되므로 화면을 옮겨도 이전 위치가 남는다.
 * 새 화면은 맨 위에서, 뒤로가기로 돌아온 화면은 떠날 때 위치로(location.key 기준).
 */
const scrollMemory = new Map<string, number>();
function useScrollRestore(mainRef: React.RefObject<HTMLElement>) {
  const location = useLocation();
  React.useLayoutEffect(() => {
    const el = mainRef.current;
    if (!el) return undefined;
    const key = location.key;
    el.scrollTop = scrollMemory.get(key) ?? 0;
    return () => { scrollMemory.set(key, el.scrollTop); };
  }, [location.key, mainRef]);
}

/** 본문이 40px 넘게 내려갔는지 — 큰 제목 ↔ 앱바 작은 제목 전환. 화면이 바뀌면(복원된 위치 기준) 다시 잰다. */
function useScrolledPast(mainRef: React.RefObject<HTMLElement>, px: number) {
  const { key } = useLocation();
  const [past, setPast] = React.useState(false);
  React.useEffect(() => {
    const el = mainRef.current;
    if (!el) return undefined;
    let frame = 0;
    const check = () => { frame = 0; setPast(el.scrollTop > px); };
    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(check); };
    check();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => { el.removeEventListener("scroll", onScroll); if (frame) window.cancelAnimationFrame(frame); };
  }, [mainRef, px, key]);
  return past;
}

/**
 * 화면 전환 — 경로가 바뀌면 본문(main)을 opacity 0→1, translateX 8px→0 (200ms).
 * Web Animations API 라 끝나면 transform 이 남지 않는다(fixed 자손 위치·스크롤 복원 영향 없음 — 200ms 동안만 transform).
 */
function usePageTransition(mainRef: React.RefObject<HTMLElement>) {
  const { pathname } = useLocation();
  React.useLayoutEffect(() => {
    const el = mainRef.current;
    if (!el || typeof el.animate !== "function") return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const anim = el.animate(
      [{ opacity: 0, transform: "translateX(8px)" }, { opacity: 1, transform: "translateX(0)" }],
      { duration: 200, easing: EASE.out },
    );
    return () => anim.cancel();
  }, [pathname, mainRef]);
}

/**
 * 제목 대체 — 메뉴 정보도 PageHeader 제목도 없는 내부 화면(예: /crm/api-console)은 본문의 첫 h1(또는 [data-page-title]) 글자를 쓴다.
 * 본문이 늦게 그려지거나 바뀌는 것을 MutationObserver 로 따라간다(150ms 디바운스). 30자 넘으면 줄임. 페이지 h1 은 숨기지 않는다.
 */
function useDerivedTitle(mainRef: React.RefObject<HTMLElement>, enabled: boolean): string | null {
  const { pathname } = useLocation();
  const [derived, setDerived] = React.useState<string | null>(null);
  React.useEffect(() => {
    const main = mainRef.current;
    setDerived(null);
    if (!enabled || !main) return undefined;
    let timer = 0;
    const scan = () => {
      const el = main.querySelector<HTMLElement>("[data-page-title], h1");
      const text = (el?.textContent || "").replace(/\s+/g, " ").trim();
      setDerived(text ? (text.length > 30 ? `${text.slice(0, 30)}…` : text) : null);
    };
    const schedule = () => { window.clearTimeout(timer); timer = window.setTimeout(scan, 150); };
    scan();
    const mo = new MutationObserver(schedule);
    mo.observe(main, { childList: true, subtree: true, characterData: true });
    return () => { window.clearTimeout(timer); mo.disconnect(); };
  }, [mainRef, enabled, pathname]);
  return derived;
}

export default function MobileShell({
  title,
  onBack,
  children,
}: {
  /** 비우면 화면이 올린 제목(PageHeader), 그것도 없으면 "Colosseum Vault" */
  title?: React.ReactNode;
  onBack?: () => void;
  children: React.ReactNode;
}) {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const current = activeTab(pathname);
  // 화면이 올린 제목(PageHeader) — 화면이 사라지면 PageHeader 정리(cleanup)가 비운다.
  const [pageTitle, setPageTitle] = React.useState<string | null>(null);
  const shellApi = React.useMemo(() => ({ setTitle: setPageTitle }), []);
  const mainRef = React.useRef<HTMLElement>(null);
  useShellClass();
  useKeyboardOpen();
  useScrollRestore(mainRef);
  usePageTransition(mainRef);
  // 큰 제목은 상세가 아닌 화면(뒤로가기 없음)에서만. 상세는 지금처럼 앱바 작은 제목.
  const largeTitle = !onBack;
  const scrolled = useScrolledPast(mainRef, 40);
  const derivedTitle = useDerivedTitle(mainRef, !title && !pageTitle);
  const shownTitle = title ?? pageTitle ?? derivedTitle ?? "Colosseum Vault";
  const [fabMode, setFabMode] = React.useState<FabMode>(null);
  const activeIndex = MOBILE_TABS.findIndex((tab) => tab.to === current);
  return (
    <MobileShellContext.Provider value={shellApi}>
      <div className="ds-scope fixed inset-0 flex flex-col bg-canvas text-ink" data-mobile-shell data-fab={fabMode ?? undefined}>
        <header className="flex-shrink-0 border-0 border-b border-solid border-hairline bg-canvas pt-[env(safe-area-inset-top)]">
          <div className="flex h-12 items-center gap-1 px-1">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                aria-label={t("뒤로", "Back")}
                className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-control text-ink active:bg-surface-hover"
              >
                <ChevronLeft size={22} aria-hidden />
              </button>
            ) : (
              <span className="w-3 flex-shrink-0" aria-hidden />
            )}
            <h1
              className={`m-0 min-w-0 flex-1 truncate text-title-sm font-semibold text-ink transition-opacity duration-fast ease-out motion-reduce:transition-none ${largeTitle && !scrolled ? "opacity-0" : "opacity-100"}`}
              data-crm-i18n-skip="true"
            >
              {shownTitle}
            </h1>
            {/* 앱 안 알림(배정·댓글·@멘션·기한) — PC 상단 바와 같은 벨. 모양·팝오버 폭은 mobileShell.css */}
            <div className="mobile-appbar-bell flex-shrink-0 pr-1">
              <NotificationBell />
            </div>
          </div>
        </header>

        {/* 본문 영역 — 당겨서 새로고침 표시기가 main 위에 겹치도록 relative 래퍼 */}
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* 넓은 표·A4 문서가 잘리지 않게 가로 스크롤 허용 — 데스크톱 main 과 같다 */}
          <main ref={mainRef} className="relative min-h-0 flex-1 overflow-auto overscroll-contain" style={{ background: "var(--crm-surface-1)", WebkitOverflowScrolling: "touch" }}>
            {largeTitle && (
              // 큰 제목(iOS) — 앱바 h1 과 같은 글자라 화면 읽기에서는 숨긴다(제목은 h1 이 맡는다)
              <div
                aria-hidden
                data-mobile-large-title=""
                data-crm-i18n-skip="true"
                className={`truncate px-4 pb-1 pt-3 text-[28px] font-bold leading-[34px] text-ink transition-opacity duration-fast ease-out motion-reduce:transition-none ${scrolled ? "opacity-0" : "opacity-100"}`}
              >
                {shownTitle}
              </div>
            )}
            {children}
          </main>
          <PullToRefresh mainRef={mainRef} />
        </div>

        <MobileFab mainRef={mainRef} onModeChange={setFabMode} />

        <nav data-mobile-tabbar aria-label={t("주요 메뉴", "Main menu")} className="flex-shrink-0 border-0 border-t border-solid border-hairline bg-canvas pb-[env(safe-area-inset-bottom)]">
          <ul className="relative m-0 flex list-none p-0">
            {/* 알약 인디케이터 — 탭 폭(20%) 칸을 활성 탭으로 옮기고 그 가운데 56×28 알약(아이콘 뒤) */}
            <li
              aria-hidden
              className="pointer-events-none absolute left-0 top-[5px] flex w-1/5 justify-center transition-transform ease-pop motion-reduce:transition-none"
              style={{ transform: `translateX(${Math.max(activeIndex, 0) * 100}%)`, transitionDuration: "320ms" }}
            >
              <span className="block h-7 w-14 rounded-pill bg-clblue-100" />
            </li>
            {MOBILE_TABS.map(({ to, icon: Icon, ko, en }) => {
              const on = current === to;
              return (
                <li key={to} className="relative min-w-0 flex-1">
                  <Link
                    to={to}
                    aria-current={on ? "page" : undefined}
                    className={`flex h-14 flex-col items-center justify-center gap-0.5 text-caption no-underline ${on ? "font-semibold text-primary-strong" : "text-ink-muted"}`}
                  >
                    <Icon size={22} aria-hidden />
                    <span>{t(ko, en)}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </MobileShellContext.Provider>
  );
}
